import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ReportRecordsTable from '../components/ReportRecordsTable';
import { Modal, Button, FormGroup, FormLabel } from '../components/adminModals';
import { exportApprovalReportPdf } from '../utils/exportReportPdf';
import { useNotification } from '../../sharedComponents/Notifications';
import {
    reportBatchService,
    ApprovalRequestItem,
    ApprovalReportData,
    ApprovalStatus,
    APPROVAL_STATUS_LABELS,
} from '../services/adminAuth';
import { ArrowLeft, CheckCircle, XCircle, Download } from 'lucide-react';

const approvalStatusStyle = (status: number): string => {
    switch (status) {
        case ApprovalStatus.Pending: return 'bg-yellow-50 text-yellow-700';
        case ApprovalStatus.Approved: return 'bg-green-50 text-green-700';
        case ApprovalStatus.Rejected: return 'bg-red-50 text-red-700';
        default: return 'bg-gray-100 text-gray-700';
    }
};

function ApprovalView() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const request = (location.state as { request?: ApprovalRequestItem } | null)?.request ?? null;
    const { showNotification } = useNotification();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<ApprovalReportData | null>(null);

    // Process (approve / reject) modal state
    const [processOpen, setProcessOpen] = useState(false);
    const [processApprove, setProcessApprove] = useState(true);
    const [comment, setComment] = useState('');
    const [processing, setProcessing] = useState(false);

    const openProcess = (approve: boolean) => {
        setProcessApprove(approve);
        setComment('');
        setProcessOpen(true);
    };

    const handleProcess = async () => {
        if (!request) return;
        if (!processApprove && !comment.trim()) {
            showNotification('warning', { title: 'Validation Error', message: 'Please provide a reason for rejection.' });
            return;
        }
        setProcessing(true);
        try {
            const res = await reportBatchService.processApprovalRequest({
                approvalRequestId: request.id,
                isApproved: processApprove,
                comment: comment.trim(),
            });
            if (res.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: `Request ${processApprove ? 'approved' : 'rejected'} successfully`,
                    timer: 3000,
                });
                setProcessOpen(false);
                navigate('/admin/approvals');
            } else {
                showNotification('error', { title: 'Error', message: res.message || 'Failed to process the request' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An error occurred while processing the request' });
        } finally {
            setProcessing(false);
        }
    };

    useEffect(() => {
        const fetchItems = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const response = await reportBatchService.getReportBatchItems(id);
                if (response.isSuccess) {
                    setData(response.data);
                } else {
                    showNotification('error', { title: 'Error', message: response.message || 'Failed to fetch batch items' });
                }
            } catch (err) {
                showNotification('error', { title: 'Error', message: 'An error occurred while fetching batch items' });
            } finally {
                setLoading(false);
            }
        };
        fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    return (
        <div className="w-full">
            <div className="flex flex-wrap -mx-[15px]">
                <div className="w-full px-[15px]">
                    <button
                        onClick={() => navigate('/admin/approvals')}
                        className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <ArrowLeft size={16} />
                        Back to Approvals
                    </button>

                    <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                                {request?.title || 'Approval Request'}
                            </h1>
                            <div className="flex flex-col items-start gap-1.5 text-sm text-gray-500">
                                {request?.nameOfRequestedUser && <span><span className="font-semibold text-gray-700">Requested by:</span> {request.nameOfRequestedUser}</span>}
                                <div className="flex items-center gap-3">
                                    {request?.periodLabel && <span>{request.periodLabel}</span>}
                                    {request && (
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${approvalStatusStyle(request.status)}`}>
                                            {APPROVAL_STATUS_LABELS[request.status] || 'Unknown'}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {request?.remark && (
                                <p className="text-xs text-gray-500 italic mt-2">Remark: {request.remark}</p>
                            )}
                        </div>

                        {request?.status === ApprovalStatus.Pending && (
                            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                                <Button variant="danger" onClick={() => openProcess(false)} className="w-full sm:w-auto flex items-center justify-center gap-2">
                                    <XCircle size={16} /> Reject
                                </Button>
                                <Button variant="success" onClick={() => openProcess(true)} className="w-full sm:w-auto flex items-center justify-center gap-2">
                                    <CheckCircle size={16} /> Accept
                                </Button>
                            </div>
                        )}
                        {(request?.status === ApprovalStatus.Approved || request?.status === ApprovalStatus.Rejected) && (
                            <Button variant="white" onClick={() => request && exportApprovalReportPdf(request, showNotification)} className="w-full lg:w-auto flex items-center justify-center gap-2">
                                <Download size={16} /> Export Report
                            </Button>
                        )}
                    </div>

                    <div className="bg-white rounded-[8px] shadow-[0_1px_3px_rgba(0,0,0,0.1)] px-[16px] sm:px-[24px] py-[16px] sm:py-[20px]">
                        <div className="mb-4">
                            <h2 className="text-[16px] sm:text-[18px] font-[700] text-[#0A0A0A] m-0 mb-1">Records</h2>
                            <p className="text-xs sm:text-sm text-gray-500">
                                {data ? `${data.totalRecords} record${data.totalRecords === 1 ? '' : 's'} in this batch` : 'Loading records…'}
                            </p>
                        </div>
                        <ReportRecordsTable data={data} loading={loading} reportType={request?.reportType} emptyMessage="No records in this batch." />
                    </div>

                    {/* Approve / Reject Modal */}
                    <Modal
                        isOpen={processOpen}
                        onClose={() => { setProcessOpen(false); setComment(''); }}
                        title={processApprove ? 'Approve Request' : 'Reject Request'}
                        description={`${processApprove ? 'Approve' : 'Reject'} "${request?.title || 'this request'}"${request?.nameOfRequestedUser ? ` requested by ${request.nameOfRequestedUser}` : ''}.`}
                    >
                        <div>
                            <FormGroup>
                                <FormLabel required={!processApprove}>Comment{processApprove ? ' (optional)' : ''}</FormLabel>
                                <textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    rows={4}
                                    placeholder={processApprove ? 'Add an optional comment...' : 'Provide a reason for rejection...'}
                                    disabled={processing}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                />
                            </FormGroup>

                            <div className="flex gap-3 mt-6">
                                <Button variant="white" onClick={() => { setProcessOpen(false); setComment(''); }} className="flex-1" disabled={processing}>
                                    Cancel
                                </Button>
                                <Button
                                    variant={processApprove ? 'success' : 'danger'}
                                    onClick={handleProcess}
                                    className="flex-1"
                                    disabled={processing}
                                >
                                    {processing ? 'Processing...' : processApprove ? 'Approve' : 'Reject'}
                                </Button>
                            </div>
                        </div>
                    </Modal>
                </div>
            </div>
        </div>
    );
}

export default ApprovalView;
